/**
 * Meta Pixel events, on top of the PageView the base code in index.html fires.
 *
 * Three standard events, so ads can optimise for what matters:
 *   Lead              the child's avatar exists (the photo step paid off)
 *   InitiateCheckout  the parent tapped Start on the paywall
 *   Purchase          back from Stripe with a completed payment
 *
 * And one of our own, through `trackCustom`:
 *   Second_screen_presented   the parent left the opening screen and the funnel
 *                             proper began — the first drop-off worth watching
 *
 * The pixel is a global the base snippet installs; when it is blocked (an ad
 * blocker, no script) `track` is a no-op, so nothing here can break the funnel.
 * The plan is remembered across the Stripe round trip in sessionStorage, which
 * is what lets Purchase carry a value; a Checkout session id keys the guard
 * that stops a reload of the success page from counting twice.
 */
import type { PlanId } from './billing';

type Fbq = (cmd: 'track' | 'trackCustom', event: string, params?: Record<string, unknown>) => void;

const send = (cmd: 'track' | 'trackCustom', event: string, params?: Record<string, unknown>): void => {
  const fbq = (window as unknown as { fbq?: Fbq }).fbq;
  if (typeof fbq !== 'function') return;
  try {
    fbq(cmd, event, params);
  } catch {
    /* the pixel's problem, not the funnel's */
  }
};

/** One of Meta's STANDARD events — the fixed list ads optimise against. */
export function track(event: string, params?: Record<string, unknown>): void {
  send('track', event, params);
}

/**
 * An event of our own naming.
 *
 * `track` is only for Meta's standard list; anything else sent through it is
 * logged as an invalid standard event and shows up in the pixel's diagnostics
 * as an error. `trackCustom` is the verb for a name we invented, and it lands
 * in Events Manager the same way — usable for audiences and custom
 * conversions.
 */
export function trackCustom(event: string, params?: Record<string, unknown>): void {
  send('trackCustom', event, params);
}

/** USD, tax-exclusive, matching the live Stripe prices. */
export const PLAN_AMOUNT: Record<PlanId, number> = { year: 119, month: 20, test: 1 };

const PLAN_KEY = 'ct.plan';

export function rememberPlan(plan: PlanId): void {
  try { sessionStorage.setItem(PLAN_KEY, plan); } catch { /* private mode */ }
}

export function recallPlan(): PlanId | null {
  try {
    const v = sessionStorage.getItem(PLAN_KEY);
    return v === 'year' || v === 'month' || v === 'test' ? v : null;
  } catch {
    return null;
  }
}

/** True the first time this Checkout session is seen; false on any reload after. */
export function firstSightOf(sessionId: string): boolean {
  const key = `ct.purchased.${sessionId}`;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}
